import { useQuery } from '@tanstack/react-query'
import { apiClient } from '@/api/client'
import { endpoints } from '@/api/endpoints'
import type {
  AlertHistoryEntry,
  AnalyticsBounceResponse,
  AnalyticsResponse,
  BillingSummary,
  BouncingDomain,
  EngineHealth,
  OverviewStats,
  PaginatedResponse,
  VolumeDataPoint,
} from '@/lib/types'

const OVERVIEW_REFETCH_MS = 60_000
const CHART_REFETCH_MS = 300_000
const ALERTS_REFETCH_MS = 60_000

function todayISO(): string {
  return new Date().toISOString().slice(0, 10)
}

function twentyFourHoursAgoISO(): string {
  const d = new Date()
  d.setHours(d.getHours() - 24)
  return d.toISOString()
}

export function useOverviewStats(): ReturnType<typeof useQuery<OverviewStats>> {
  return useQuery({
    queryKey: ['overview', 'stats'],
    queryFn: async (): Promise<OverviewStats> => {
      const [health, billing, analytics] = await Promise.allSettled([
        apiClient.get<EngineHealth>(endpoints.engine.health),
        apiClient.get<BillingSummary>(endpoints.billing.summary),
        apiClient.get<AnalyticsResponse>(
          endpoints.analytics.overview({ from: todayISO(), to: todayISO(), granularity: 'day' }),
        ),
      ])

      const engineData = health.status === 'fulfilled' ? health.value : null
      const billingData = billing.status === 'fulfilled' ? billing.value : null
      const analyticsData = analytics.status === 'fulfilled' ? analytics.value : null

      const activeAccounts = billingData
        ? Object.values(billingData.accounts_by_tier).reduce((sum, t) => sum + t.count, 0)
        : 0

      return {
        emails_today: analyticsData?.sent ?? 0,
        delivery_rate: analyticsData?.delivery_rate ?? 0,
        bounce_rate: analyticsData?.bounce_rate ?? 0,
        queue_depth: engineData?.queue_depth ?? -1,
        uptime_seconds: engineData?.uptime_seconds ?? -1,
        active_accounts: activeAccounts,
      }
    },
    refetchInterval: OVERVIEW_REFETCH_MS,
  })
}

export function useVolumeChart(): ReturnType<typeof useQuery<VolumeDataPoint[]>> {
  return useQuery({
    queryKey: ['overview', 'volume'],
    queryFn: () =>
      apiClient.get<VolumeDataPoint[]>(
        endpoints.analytics.overview({
          from: twentyFourHoursAgoISO(),
          to: new Date().toISOString(),
          granularity: 'hour',
        }),
      ),
    refetchInterval: CHART_REFETCH_MS,
  })
}

export function useRecentAlerts(): ReturnType<typeof useQuery<AlertHistoryEntry[]>> {
  return useQuery({
    queryKey: ['overview', 'alerts'],
    queryFn: async (): Promise<AlertHistoryEntry[]> => {
      const resp = await apiClient.get<PaginatedResponse<AlertHistoryEntry>>(
        endpoints.alerts.history({ per_page: 10 }),
      )
      return resp.data
    },
    refetchInterval: ALERTS_REFETCH_MS,
  })
}

export function useTopBouncingDomains(): ReturnType<typeof useQuery<BouncingDomain[]>> {
  return useQuery({
    queryKey: ['overview', 'bouncing-domains'],
    queryFn: async (): Promise<BouncingDomain[]> => {
      const resp = await apiClient.get<AnalyticsBounceResponse>(
        endpoints.analytics.bounces({
          from: twentyFourHoursAgoISO(),
          to: new Date().toISOString(),
        }),
      )
      return resp.domains.slice(0, 5)
    },
    refetchInterval: CHART_REFETCH_MS,
  })
}
